import { Alert, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { CheckboxField } from "components/Forms/CheckboxField";
import { Control, Controller, useWatch } from "react-hook-form";

import { episodicReliefAdvisory } from "../EpisodicEvents/episodicDisplay";
import { FormSection, WasteContainerFormValues } from "./containerFormParts";
import { JoinableEpisodicEvent } from "./episodicAttachChoice";

/** The event's reason in quotes, ending the sentence inside them. A reason
 * that already ends in a period doesn't get a second one. */
function quotedReason(reason: string): string {
  return reason.endsWith(".") ? `"${reason}"` : `"${reason}."`;
}

/** The width of a small MUI checkbox plus its padding — what the label text
 * starts at, and so where anything explaining that label should start too. */
const EPISODIC_HELPER_INDENT = "34px";

function eventOptionLabel(event: JoinableEpisodicEvent): string {
  return `${event.reason} (${event.type.toLowerCase()}, started ${event.startDate})`;
}

/**
 * The attach question, asked of a container being created at a facility that
 * has an episodic event running.
 *
 * Asked here rather than left to the episodic page because this is where the
 * waste is being recorded: a user adding a drum from the container list during
 * a cleanout has no reason to know the event page exists, and a container that
 * misses its event silently clocks to the facility's standing limit instead of
 * the event's — a longer deadline than the regulation grants that waste.
 *
 * The question is about the FACILITY, not the waste stream. Any stream can
 * accumulate under an event, so gating it on a stream selection would hide it
 * from exactly the containers most likely to be the surge's — which is also
 * why it sits directly under the facility section rather than beside the
 * waste, and ahead of every field the answer changes.
 */
export function ContainerEpisodicSection({
  control,
  events,
}: {
  control: Control<WasteContainerFormValues>;
  events: JoinableEpisodicEvent[];
}) {
  const attach = useWatch({ control, name: "attachToEpisodicEvent" });
  const chosenEventId = useWatch({ control, name: "episodicEventId" });

  if (events.length === 0) {
    return null;
  }

  const single = events.length === 1 ? events[0] : null;
  const target = single ?? events.find((event) => event.id === chosenEventId);
  const advisory = target ? episodicReliefAdvisory(target.relief) : null;

  return (
    <FormSection label="Episodic event">
      <Grid size={{ xs: 12 }}>
        <Stack spacing={1}>
          <CheckboxField
            name="attachToEpisodicEvent"
            label={
              single
                ? `This facility has an active episodic event — ${quotedReason(single.reason)} Attach this container to it.`
                : "This facility has active episodic events. Attach this container to one of them."
            }
            control={control}
            noErrorDisplay
          />
          {/* Which event only becomes a question when there are two to choose
              between — a facility may run one planned and one unplanned at
              once, and the two carry different deadlines. */}
          {attach && !single && (
            <Controller
              name="episodicEventId"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  select
                  fullWidth
                  required
                  label="Episodic event"
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value || null)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                >
                  {events.map((event) => (
                    <MenuItem key={event.id} value={event.id}>
                      {eventOptionLabel(event)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          )}
          {/* Indented to the label's text, not the checkbox: this explains
              what was just ticked, so it reads as a continuation of the
              sentence above rather than a second, unrelated control. */}
          {attach && (
            <Alert severity="info" sx={{ ml: EPISODIC_HELPER_INDENT }}>
              The container will clock to the event&apos;s 60-day removal deadline instead of the
              facility&apos;s usual storage limit, and its dates are limited to the event&apos;s
              window.
              {advisory && ` ${advisory.note}`}
            </Alert>
          )}
          {!attach && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: EPISODIC_HELPER_INDENT }}
            >
              Leave this unchecked for routine waste — it will accumulate under the facility&apos;s
              usual limits.
            </Typography>
          )}
        </Stack>
      </Grid>
    </FormSection>
  );
}
